import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function MyTeam() {
  const { user } = useApp();
  const { t } = useI18n();
  const [subordinates, setSubordinates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
  });

  const referralLink = user?.referral_code 
    ? `https://t.me/WORKIFYPL_bot?start=ref_${user.referral_code}`
    : null;

  useEffect(() => {
    loadSubordinates();
  }, []);

  async function loadSubordinates() {
    try {
      const { data } = await api.get('/manager/my-clients');
      setSubordinates(data || []);
    } catch (e) {
      try {
        const { data } = await api.get('/me/subordinates');
        setSubordinates(data || []);
      } catch (e2) {
        setSubordinates([]);
      }
    } finally {
      setLoading(false);
    }
  }

  async function createClient() {
    if (!form.first_name || !form.last_name) {
      setMessage({ type: 'error', text: 'Имя и фамилия обязательны' });
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/manager/create-client', form);
      setNewClient(data);
      setForm({ first_name: '', last_name: '', phone: '', email: '' });
      setShowForm(false);
      loadSubordinates();
      setMessage({ type: 'success', text: '✅ Клиент создан!' });
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка создания' });
    } finally {
      setCreating(false);
    }
  }

  async function resendInvite(clientId) {
    try {
      const { data } = await api.post(`/manager/resend-invite/${clientId}`);
      setNewClient({ ...data, id: clientId });
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    }
  }

  function copyLink() {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function shareLink() {
    if (referralLink && navigator.share) {
      navigator.share({
        title: 'Присоединяйся к WORKIFYPL',
        text: 'Регистрируйся по моей ссылке!',
        url: referralLink,
      });
    } else {
      copyLink();
    }
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const isManager = ['manager', 'admin', 'superadmin'].includes(user?.role);

  return (
    <div className="page">
      <BackButton />
      <h1>👥 Моя команда</h1>

      {message && (
        <div style={{
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          background: message.type === 'error' ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)',
        }}>
          {message.text}
        </div>
      )}

      {/* Referral Link */}
      {referralLink && (
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 18 }}>
            🔗 Приглашай клиентов
          </div>
          <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 12 }}>
            Отправь эту ссылку — новые пользователи автоматически станут твоими клиентами
          </p>
          
          <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            wordBreak: 'break-all',
            marginBottom: 12,
          }}>
            {referralLink}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              style={{ flex: 1, background: 'rgba(255,255,255,0.2)' }}
              onClick={copyLink}
            >
              {copied ? '✅ Скопировано!' : '📋 Копировать'}
            </button>
            <button
              className="btn"
              style={{ flex: 1, background: 'rgba(255,255,255,0.2)' }}
              onClick={shareLink}
            >
              📤 Поделиться
            </button>
          </div>

          {user?.manager_commission_rate > 0 && (
            <div style={{
              marginTop: 12,
              padding: 8,
              background: 'rgba(16,185,129,0.3)',
              borderRadius: 6,
              fontSize: 13,
              textAlign: 'center',
            }}>
              💰 Твоя комиссия: <b>{user.manager_commission_rate}%</b> от прибыли клиентов
            </div>
          )}
        </div>
      )}

      {/* Add Client Button */}
      {!showForm && !newClient && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: 16 }}
          onClick={() => setShowForm(true)}
        >
          ➕ Добавить клиента вручную
        </button>
      )}

      {/* Create Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
        }}>
          <h3 style={{ marginTop: 0 }}>👤 Новый клиент</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Имя *</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="Иван"
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Фамилия *</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Иванов"
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>{t('common.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+48 123 456 789"
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>{t('common.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="client@email.com"
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={createClient} disabled={creating}>
              {creating ? '⏳...' : '✅ Создать'}
            </button>
            <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* New Client Success Modal */}
      {newClient && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
        }}>
          <h3 style={{ marginTop: 0 }}>✅ Клиент создан!</h3>
          <p><b>{newClient.first_name} {newClient.last_name}</b></p>
          
          <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Ссылка для входа:</div>
            <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{newClient.invite_link}</div>
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              style={{ flex: 1, background: 'rgba(255,255,255,0.2)' }}
              onClick={() => {
                navigator.clipboard.writeText(newClient.invite_link);
                setMessage({ type: 'success', text: '✅ Ссылка скопирована!' });
              }}
            >
              📋 Копировать
            </button>
            <button
              className="btn"
              style={{ flex: 1, background: 'rgba(255,255,255,0.2)' }}
              onClick={() => setNewClient(null)}
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12,
        marginBottom: 24,
      }}>
        <div style={{
          background: 'rgba(16,185,129,0.1)',
          borderRadius: 12,
          padding: 16,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, fontWeight: 'bold' }}>{subordinates.length}</div>
          <div style={{ opacity: 0.7 }}>Клиентов</div>
        </div>
        <div style={{
          background: 'rgba(139,92,246,0.1)',
          borderRadius: 12,
          padding: 16,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, fontWeight: 'bold' }}>
            {user?.manager_commission_rate || 0}%
          </div>
          <div style={{ opacity: 0.7 }}>{t('common.commission')}</div>
        </div>
      </div>

      {/* Subordinates List */}
      <h3>📋 Мои клиенты</h3>
      
      {subordinates.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
          <p style={{ opacity: 0.7 }}>Пока нет клиентов</p>
          <p style={{ fontSize: 13, opacity: 0.5 }}>Добавь вручную или поделись ссылкой!</p>
        </div>
      ) : (
        <div className="list">
          {subordinates.map(s => (
            <div key={s.id} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>
                    {s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Без имени'}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.7 }}>
                    {s.phone && <span>{s.phone} • </span>}
                    {s.email && <span>{s.email}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {s.is_temp && (
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      background: '#f59e0b',
                    }}>
                      ⏳ Не вошёл
                    </span>
                  )}
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    background: s.profile_status === 'verified' ? '#10b981' : '#6b7280',
                  }}>
                    {s.profile_status === 'verified' ? '✅' : s.profile_status}
                  </span>
                </div>
              </div>
              
              {s.is_temp && (
                <button
                  className="btn btn-small"
                  style={{ marginTop: 8, fontSize: 12 }}
                  onClick={() => resendInvite(s.id)}
                >
                  🔄 Получить ссылку для входа
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
